import * as React from 'react';
import {useEffect} from 'react';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {DatePicker, DateTimePicker} from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import {InputAdornment} from "@mui/material";
import {useBodyScrollLock} from "../../hooks/useBodyScrollLock";
import "../datepicker/simmsDatePicker.css";

/**
 * <div>SimmsDatePicker</div>의 형태라 th_title에서 넘겨준 constraint.required를 받지 못함
 * */
export default function ITER_SimmsDatePicker({
    data,
    style,
    onChange,
    onUpdate,
    update,
    constraint,
    auth,
    readonly,
    placeHolder,
    time,
    theme = "ios",
}) {
    const toDayjsOrNull = (v) => {
        if (!v) return null;
        const d = dayjs(v);
        return d.isValid() ? d : null;
    };

    const [value, setValue] = React.useState(toDayjsOrNull(data));
    const fromDateSelect = React.useRef(false);
    const [open, setOpen] = React.useState(false); // 팝업 열림 상태 관리
    const PickerComponent = time ? DateTimePicker : DatePicker;

    const format = time ? "YYYY-MM-DD HH:mm" : "YYYY-MM-DD";
    const displayFormat = time ? 'YYYY년 MM월 DD일 HH시 mm분' : 'YYYY년 MM월 DD일';

    useEffect(() => {
        setValue(toDayjsOrNull(data));
    }, [data]);

    useBodyScrollLock(open);

    // 등록, 수정 시 날짜선택
    const handleDateSelect = (newDate) => {
        fromDateSelect.current = true;

        if (!newDate) {
            onChange(null);
            setValue(null);
            return;
        }

        const updatedDate = toDayjsOrNull(newDate);

        if(!update) {
            onChange(updatedDate.format(format));
        }
        setValue(updatedDate);
    };

    // 하나의 함수로 제어 시 clear, cancel 등 제어가 불안정해져 ok 누를 시 수정페이지에서만 업데이트 기능 동작
    const handleDateUpdate = (newDate) => {
        onUpdate(newDate ? toDayjsOrNull(newDate).format(format) : null);
    };

    const editable = !!auth && !readonly;
    const readonlySource = value ?? toDayjsOrNull(data);
    const readonlyText =
        readonlySource && readonlySource.isValid()
            ? readonlySource.format(displayFormat)
            : "";

    /** 보기 모드(auth 없음): KV 테이블과 동일한 읽기 전용 텍스트 */
    if (!editable) {
        return (
            <span className="asset-detail-kv-value" style={style}>
                {readonlyText || "\u00A0"}
            </span>
        );
    }

    return (
        // value : 값
        // format : 날짜포멧형식
        // reduceAnimations : 과도한 애니메이션 삭제
        // showDaysOutsideCurrentMonth : 현재 달 전후로 날짜표시
        // slots datepicker의 상단, 하단부 필드{toolbar, actionBar}
        // slotProps : 화면 input props
        <div
            className={[
                "simms-date-picker-cell",
                "simms-date-picker",
                theme === "ios" ? "simms-date-picker--ios simms-date-picker--kv-typography" : "",
                time ? "simms-date-picker--time" : "",
                open ? "is-open" : "",
                value ? "is-selected" : "",
            ].filter(Boolean).join(" ")}
        >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <PickerComponent
                ampm={false}
                views={['year', 'month', 'day', 'hours', 'minutes']}
                timeSteps={{ hours: 1, minutes: 1 }}
                value={value}
                format={format}
                enableAccessibleFieldDOMStructure={false}
                disableOpenPicker
                className="simms-date-picker-control"
                onChange={(newValue) => handleDateSelect(newValue)}
                reduceAnimations={true}
                showDaysOutsideCurrentMonth
                closeOnSelect={false}
                open={open}
                // 등록페이지 : 날짜 클릭 시 닫힘(시, 분타입일 시 안닫히도록 수정)
                // 수정페이지 : 날짜 클릭해도 닫히지 않음
                onClose={() => {
                    if (fromDateSelect.current) {
                        fromDateSelect.current = false;
                        if (update) return;
                    }
                    setOpen(false);
                }}
                slots={{
                    actionBar: (props) => (
                        <div className={"flex px-[26px] text-[13px] font-bold mb-[15px] datepicker-mui-action"}>
                            <p className={"cursor-pointer"} onClick={() => {
                                if(!constraint?.required){
                                    if(update){
                                        // eslint-disable-next-line no-restricted-globals
                                        if(confirm("데이터를 초기화하시겠습니까?")) { // 수정페이지에서 사용자가 "OK"를 누른 경우에만 실행
                                            handleDateSelect(null);
                                            handleDateUpdate(null);
                                        }
                                    }else{
                                        handleDateSelect(null);
                                    }
                                }else{
                                    alert("필수값이라 초기화할수 없습니다.")
                                }
                            }}
                            >CLEAR</p>
                            <p className={"cursor-pointer ml-auto"} onClick={() => {
                                    setValue(data ? dayjs(data) : null);
                                    props.onCancel();
                                }}
                            >CANCEL</p>
                            <p className="ml-3 cursor-pointer" onClick={() => {
                                if (update) {
                                    handleDateUpdate(value);
                                }
                                props.onAccept();
                                fromDateSelect.current = false;
                            }}>OK</p>
                        </div>
                    )
                }}
                slotProps={{
                    textField: {
                        size: 'small',
                        color: 'none',
                        onClick: () => {
                            if (!readonly && auth) setOpen(!open);
                        },
                        fullWidth: false,
                        sx: {
                            width: 'fit-content',
                            margin: 0,
                            display: 'inline-flex',
                            ...style,
                        },
                        InputProps: {
                            endAdornment: null,
                            startAdornment: (
                                <InputAdornment position="start">
                                    <CalendarTodayIcon fontSize="inherit" />
                                </InputAdornment>
                            ),
                        },
                        inputProps: {
                            readOnly: true,
                            value: value
                                ? dayjs(value).format(displayFormat)
                                : placeHolder ?? '날짜를 선택하세요',
                        },
                    },
                    popper: {
                        className : "ignore-click",
                        sx: {
                            marginTop : "3px !important", // 팝업과 Input 사이에 여백 추가
                            marginBottom : "3px !important", // 팝업과 Input 사이에 여백 추가

                        },
                    },
                }}
            />
        </LocalizationProvider>
        </div>
    );
}
